/**
 * Impact Visualization Component
 *
 * Visual display of optimization recommendation impact analysis
 *
 * Features:
 * - Before/After comparison
 * - ROI calculation display
 * - Time/cost savings visualization
 * - Risk assessment indicators
 * - Break-even timeline
 */

import React from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Badge,
  Progress,
  Grid,
  Paper,
  ThemeIcon,
  RingProgress,
  Divider,
  Alert
} from '@mantine/core';
import {
  IconTrendingUp,
  IconTrendingDown,
  IconClock,
  IconCoin,
  IconAlertTriangle,
  IconShield,
  IconCheck,
  IconArrowRight,
  IconChartBar
} from '@tabler/icons-react';
import type { ImpactAnalysis } from '../hooks/useOptimizationRecommendations';

interface ImpactVisualizationProps {
  impactAnalysis: ImpactAnalysis;
  compact?: boolean;
}

export function ImpactVisualization({ impactAnalysis, compact = false }: ImpactVisualizationProps) {
  /**
   * Format time savings
   */
  const formatTimeSavings = (seconds?: number): { value: string; unit: string } => {
    if (!seconds) return { value: 'N/A', unit: '' };

    const hours = seconds / 3600;
    if (hours < 1) {
      return { value: Math.round(seconds / 60).toString(), unit: 'minutes' };
    }
    if (hours < 24) {
      return { value: hours.toFixed(1), unit: 'hours' };
    }
    const days = hours / 24;
    if (days < 30) {
      return { value: days.toFixed(1), unit: 'days' };
    }
    return { value: (days / 30).toFixed(1), unit: 'months' };
  };

  /**
   * Get impact color
   */
  const getImpactColor = (value?: number): string => {
    if (!value) return 'gray';
    if (value >= 50) return 'green';
    if (value >= 30) return 'teal';
    if (value >= 20) return 'blue';
    if (value >= 10) return 'cyan';
    return 'gray';
  };

  /**
   * Calculate overall impact score (0-100)
   */
  const calculateOverallScore = (): number => {
    let score = 0;
    let factors = 0;

    if (impactAnalysis.timeSavingsSeconds) {
      score += Math.min((impactAnalysis.timeSavingsSeconds / 3600) * 10, 25);
      factors++;
    }
    if (impactAnalysis.costSavingsPercentage) {
      score += Math.min(impactAnalysis.costSavingsPercentage, 25);
      factors++;
    }
    if (impactAnalysis.errorReductionPercentage) {
      score += Math.min(impactAnalysis.errorReductionPercentage, 25);
      factors++;
    }
    if (impactAnalysis.safetyImprovementScore) {
      score += Math.min(impactAnalysis.safetyImprovementScore * 5, 25);
      factors++;
    }

    return factors > 0 ? Math.min(score, 100) : 0;
  };

  const timeSavings = formatTimeSavings(impactAnalysis.timeSavingsSeconds);
  const overallScore = calculateOverallScore();

  if (compact) {
    return (
      <Paper p="md" withBorder>
        <Stack gap="sm">
          <Text fw={600} size="sm">Expected Impact</Text>
          <Grid>
            {impactAnalysis.timeSavingsSeconds && (
              <Grid.Col span={6}>
                <Group gap="xs">
                  <IconClock size={16} color="var(--mantine-color-blue-6)" />
                  <div>
                    <Text size="xs" c="dimmed">Time Saved</Text>
                    <Text size="sm" fw={600}>{timeSavings.value} {timeSavings.unit}</Text>
                  </div>
                </Group>
              </Grid.Col>
            )}
            {impactAnalysis.costSavingsPercentage && (
              <Grid.Col span={6}>
                <Group gap="xs">
                  <IconCoin size={16} color="var(--mantine-color-green-6)" />
                  <div>
                    <Text size="xs" c="dimmed">Cost Saved</Text>
                    <Text size="sm" fw={600}>
                      {Math.round(impactAnalysis.costSavingsPercentage)}%
                    </Text>
                  </div>
                </Group>
              </Grid.Col>
            )}
          </Grid>
          {impactAnalysis.estimatedRoi && (
            <Group gap="xs" mt="xs">
              <IconTrendingUp size={16} color="var(--mantine-color-violet-6)" />
              <Text size="sm">
                Est. ROI: <Text component="span" fw={600}>
                  ${Math.round(impactAnalysis.estimatedRoi).toLocaleString()}
                </Text>
              </Text>
            </Group>
          )}
        </Stack>
      </Paper>
    );
  }

  return (
    <Card withBorder>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Text fw={600} size="lg">Impact Analysis</Text>
          <RingProgress
            size={80}
            thickness={8}
            sections={[{ value: overallScore, color: getImpactColor(overallScore) }]}
            label={
              <Text ta="center" size="xs">
                <Text fw={700}>{Math.round(overallScore)}</Text>
                <Text size="xs" c="dimmed">Score</Text>
              </Text>
            }
          />
        </Group>

        <Divider />

        {/* Key Metrics Grid */}
        <Grid>
          {/* Time Savings */}
          {impactAnalysis.timeSavingsSeconds && (
            <Grid.Col span={6}>
              <Paper p="md" withBorder bg="blue.0">
                <Stack gap="xs">
                  <Group gap="xs">
                    <ThemeIcon color="blue" variant="light">
                      <IconClock size={18} />
                    </ThemeIcon>
                    <Text size="sm" fw={600}>Time Savings</Text>
                  </Group>
                  <Text size="xl" fw={700} c="blue">
                    {timeSavings.value} {timeSavings.unit}
                  </Text>
                  <Progress
                    value={Math.min((impactAnalysis.timeSavingsSeconds / 86400) * 100, 100)}
                    color="blue"
                    size="sm"
                  />
                  <Text size="xs" c="dimmed">
                    Per workflow execution
                  </Text>
                </Stack>
              </Paper>
            </Grid.Col>
          )}

          {/* Cost Savings */}
          {impactAnalysis.costSavingsPercentage && (
            <Grid.Col span={6}>
              <Paper p="md" withBorder bg="green.0">
                <Stack gap="xs">
                  <Group gap="xs">
                    <ThemeIcon color="green" variant="light">
                      <IconCoin size={18} />
                    </ThemeIcon>
                    <Text size="sm" fw={600}>Cost Savings</Text>
                  </Group>
                  <Text size="xl" fw={700} c="green">
                    {Math.round(impactAnalysis.costSavingsPercentage)}%
                  </Text>
                  <Progress
                    value={impactAnalysis.costSavingsPercentage}
                    color="green"
                    size="sm"
                  />
                  <Text size="xs" c="dimmed">
                    Operational cost reduction
                  </Text>
                </Stack>
              </Paper>
            </Grid.Col>
          )}

          {/* Error Reduction */}
          {impactAnalysis.errorReductionPercentage && (
            <Grid.Col span={6}>
              <Paper p="md" withBorder bg="red.0">
                <Stack gap="xs">
                  <Group gap="xs">
                    <ThemeIcon color="red" variant="light">
                      <IconAlertTriangle size={18} />
                    </ThemeIcon>
                    <Text size="sm" fw={600}>Error Reduction</Text>
                  </Group>
                  <Text size="xl" fw={700} c="red">
                    {Math.round(impactAnalysis.errorReductionPercentage)}%
                  </Text>
                  <Progress
                    value={impactAnalysis.errorReductionPercentage}
                    color="red"
                    size="sm"
                  />
                  <Text size="xs" c="dimmed">
                    Fewer errors and exceptions
                  </Text>
                </Stack>
              </Paper>
            </Grid.Col>
          )}

          {/* Safety Improvement */}
          {impactAnalysis.safetyImprovementScore && (
            <Grid.Col span={6}>
              <Paper p="md" withBorder bg="orange.0">
                <Stack gap="xs">
                  <Group gap="xs">
                    <ThemeIcon color="orange" variant="light">
                      <IconShield size={18} />
                    </ThemeIcon>
                    <Text size="sm" fw={600}>Safety Improvement</Text>
                  </Group>
                  <Text size="xl" fw={700} c="orange">
                    {impactAnalysis.safetyImprovementScore.toFixed(1)}/10
                  </Text>
                  <Progress
                    value={impactAnalysis.safetyImprovementScore * 10}
                    color="orange"
                    size="sm"
                  />
                  <Text size="xs" c="dimmed">
                    Life-critical system safety
                  </Text>
                </Stack>
              </Paper>
            </Grid.Col>
          )}
        </Grid>

        {/* Additional Metrics */}
        {(impactAnalysis.complianceGapsClosed || impactAnalysis.affectedWorkflows) && (
          <>
            <Divider />
            <Grid>
              {impactAnalysis.complianceGapsClosed && (
                <Grid.Col span={6}>
                  <Group gap="xs">
                    <ThemeIcon color="teal" variant="light" size="sm">
                      <IconCheck size={16} />
                    </ThemeIcon>
                    <div>
                      <Text size="xs" c="dimmed">Compliance Gaps Closed</Text>
                      <Text size="lg" fw={600}>{impactAnalysis.complianceGapsClosed}</Text>
                    </div>
                  </Group>
                </Grid.Col>
              )}
              {impactAnalysis.affectedWorkflows && (
                <Grid.Col span={6}>
                  <Group gap="xs">
                    <ThemeIcon color="indigo" variant="light" size="sm">
                      <IconChartBar size={16} />
                    </ThemeIcon>
                    <div>
                      <Text size="xs" c="dimmed">Affected Workflows</Text>
                      <Text size="lg" fw={600}>{impactAnalysis.affectedWorkflows}</Text>
                    </div>
                  </Group>
                </Grid.Col>
              )}
            </Grid>
          </>
        )}

        {/* ROI Section */}
        {impactAnalysis.estimatedRoi && (
          <>
            <Divider />
            <Paper p="md" withBorder bg="violet.0">
              <Stack gap="sm">
                <Group gap="xs">
                  <ThemeIcon color="violet" variant="light">
                    <IconTrendingUp size={18} />
                  </ThemeIcon>
                  <Text fw={600}>Return on Investment</Text>
                </Group>
                <Group gap="xl">
                  <div>
                    <Text size="xs" c="dimmed">Estimated ROI</Text>
                    <Text size="xl" fw={700} c="violet">
                      ${Math.round(impactAnalysis.estimatedRoi).toLocaleString()}
                    </Text>
                  </div>
                  {impactAnalysis.breakEvenDays && (
                    <div>
                      <Text size="xs" c="dimmed">Break-Even Time</Text>
                      <Text size="lg" fw={600}>
                        {impactAnalysis.breakEvenDays < 30
                          ? `${impactAnalysis.breakEvenDays} days`
                          : `${Math.round(impactAnalysis.breakEvenDays / 30)} months`}
                      </Text>
                    </div>
                  )}
                </Group>
              </Stack>
            </Paper>
          </>
        )}

        {/* Before/After Comparison */}
        {(impactAnalysis.timeSavingsSeconds || impactAnalysis.costSavingsPercentage) && (
          <>
            <Divider label="Before → After" labelPosition="center" />
            <Grid>
              {impactAnalysis.timeSavingsSeconds && (
                <Grid.Col span={12}>
                  <Group justify="space-between">
                    <Paper p="sm" withBorder style={{ flex: 1 }} bg="gray.0">
                      <Text size="xs" c="dimmed" mb="xs">Before</Text>
                      <Group gap="xs">
                        <IconClock size={16} />
                        <Text fw={600}>
                          {(timeSavings.value === 'N/A' ? 'Baseline' :
                            `+${timeSavings.value} ${timeSavings.unit}`)}
                        </Text>
                      </Group>
                    </Paper>
                    <ThemeIcon color="blue" variant="light" size="lg">
                      <IconArrowRight size={20} />
                    </ThemeIcon>
                    <Paper p="sm" withBorder style={{ flex: 1 }} bg="blue.0">
                      <Text size="xs" c="dimmed" mb="xs">After</Text>
                      <Group gap="xs">
                        <IconTrendingDown size={16} color="var(--mantine-color-green-6)" />
                        <Text fw={600} c="green">Optimized</Text>
                      </Group>
                    </Paper>
                  </Group>
                </Grid.Col>
              )}
            </Grid>
          </>
        )}

        {/* Overall Assessment */}
        <Alert
          icon={<IconCheck size={16} />}
          color={
            overallScore >= 70 ? 'green' :
            overallScore >= 50 ? 'blue' :
            overallScore >= 30 ? 'yellow' : 'orange'
          }
        >
          <Text size="sm" fw={600}>
            {overallScore >= 70 ? 'High Impact Recommendation' :
             overallScore >= 50 ? 'Medium Impact Recommendation' :
             overallScore >= 30 ? 'Moderate Impact Recommendation' :
             'Low Impact Recommendation'}
          </Text>
          <Text size="xs" mt="xs">
            Overall impact score: {Math.round(overallScore)}/100. This recommendation is expected to
            deliver measurable improvements across multiple dimensions.
          </Text>
        </Alert>
      </Stack>
    </Card>
  );
}
